/**
 * Reading errors the API sends back.
 *
 * DRF returns either `{detail: "..."}` for a whole-request problem or
 * `{field: ["..."]}` for a form. Both end up here so a screen can put the
 * sentence next to the field it belongs to, which is the only place anyone
 * reads it.
 */

export interface FormErrors {
  /** Keyed by field name, first message only — a field with two errors has one problem. */
  readonly fields: Readonly<Record<string, string>>;
  /** The request-level message, if there is one. */
  readonly detail: string | undefined;
}

const EMPTY: FormErrors = { fields: {}, detail: undefined };

function firstString(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === 'string' ? first : undefined;
  }
  return undefined;
}

export function readApiError(error: unknown): FormErrors {
  if (typeof error !== 'object' || error === null) return EMPTY;

  const status = 'status' in error ? (error as { status: unknown }).status : undefined;
  const data = 'data' in error ? (error as { data: unknown }).data : undefined;

  if (status === 'FETCH_ERROR') {
    return {
      fields: {},
      detail: 'The server did not answer. Check the connection and try again.',
    };
  }

  if (status === 403) {
    return { fields: {}, detail: 'Your account cannot do that. Ask the owner.' };
  }

  if (typeof data !== 'object' || data === null) {
    return { fields: {}, detail: 'That did not save. Try again.' };
  }

  const record = data as Record<string, unknown>;
  const fields: Record<string, string> = {};
  let detail: string | undefined;

  for (const [key, value] of Object.entries(record)) {
    const message = firstString(value);
    if (message === undefined) continue;
    if (key === 'detail' || key === 'non_field_errors') {
      detail = message;
    } else {
      fields[key] = message;
    }
  }

  return { fields, detail };
}
